// WebCrypto helpers shared by Workers and Node. No Node-only modules here.

const enc = new TextEncoder();

export function toHex(bytes: ArrayBuffer | Uint8Array): string {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (const b of u) s += b.toString(16).padStart(2, '0');
  return s;
}

export function fromHex(hex: string): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const u = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (const b of u) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromBase64(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** Constant-time comparison of two strings (same length required to be equal). */
export function timingSafeEqual(a: string, b: string): boolean {
  const ab = enc.encode(a);
  const bb = enc.encode(b);
  let diff = ab.length ^ bb.length;
  const n = Math.max(ab.length, bb.length);
  for (let i = 0; i < n; i++) diff |= (ab[i] ?? 0) ^ (bb[i] ?? 0);
  return diff === 0;
}

export async function sha256Hex(input: string): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-256', enc.encode(input)));
}

export async function sha1Hex(input: string): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-1', enc.encode(input)));
}

export async function hmacSha256Hex(key: string, input: string): Promise<string> {
  const k = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return toHex(await crypto.subtle.sign('HMAC', k, enc.encode(input)));
}

/** Keyed hash used for lookups on sensitive values (emails, payout destinations, document numbers). */
export async function blindIndex(key: string, value: string): Promise<string> {
  return hmacSha256Hex(key, value.trim().toLowerCase());
}

// --- Password hashing: PBKDF2-SHA256 via WebCrypto (bcrypt is too CPU-heavy for Workers). ---

// Cloudflare Workers reject PBKDF2 above 100,000 iterations (deriveBits throws NotSupportedError),
// so this is the ceiling here. Stored hashes carry their own count, so it can change later.
export const PBKDF2_ITERATIONS = 100_000;

/**
 * A well-formed hash at full strength that matches no password, for checking against when the
 * account doesn't exist, so a wrong email takes as long as a wrong password.
 */
export const DUMMY_PASSWORD_HASH = `pbkdf2$${PBKDF2_ITERATIONS}$AAAAAAAAAAAAAAAAAAAAAA==$AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA=`;

export async function hashPassword(password: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const bits = await pbkdf2(password, salt, PBKDF2_ITERATIONS);
  return `pbkdf2$${PBKDF2_ITERATIONS}$${toBase64(salt)}$${toBase64(bits)}`;
}

export async function verifyPassword(password: string, stored: string): Promise<boolean> {
  const [scheme, iterStr, saltB64, hashB64] = stored.split('$');
  if (scheme !== 'pbkdf2' || !iterStr || !saltB64 || !hashB64) return false;
  const bits = await pbkdf2(password, fromBase64(saltB64), Number(iterStr));
  return timingSafeEqual(toBase64(bits), hashB64);
}

async function pbkdf2(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number,
): Promise<ArrayBuffer> {
  const key = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, [
    'deriveBits',
  ]);
  return crypto.subtle.deriveBits({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, key, 256);
}

// --- Field encryption: AES-256-GCM. Key is a base64 32-byte secret from a Worker binding. ---

async function importAesKey(keyB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromBase64(keyB64), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]);
}

export async function encryptField(keyB64: string, plaintext: string): Promise<string> {
  const key = await importAesKey(keyB64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext));
  return `v1.${toBase64(iv)}.${toBase64(ct)}`;
}

export async function decryptField(keyB64: string, token: string): Promise<string> {
  const [v, ivB64, ctB64] = token.split('.');
  if (v !== 'v1' || !ivB64 || !ctB64) throw new Error('bad ciphertext format');
  const key = await importAesKey(keyB64);
  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64(ivB64) },
    key,
    fromBase64(ctB64),
  );
  return new TextDecoder().decode(pt);
}
